// =============================================================
// slideshow.js — gallery slideshow controller.
// One instance per .gallery-slideshow. GSAP fade + auto-rotate.
// Pauses when section not active. Resumes when active.
// =============================================================

const isReducedMotion = () =>
  window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

class Slideshow {
  constructor(root) {
    this.root = root;
    this.slides = [...root.querySelectorAll(".gs-slide")];
    this.dots = [...root.querySelectorAll(".gs-dot")];
    this.prevBtn = root.querySelector(".gs-prev");
    this.nextBtn = root.querySelector(".gs-next");
    this.progressBar = root.querySelector(".gs-progress-bar");
    this.counterI = root.querySelector(".gs-i");
    this.total = this.slides.length;
    this.current = 0;
    this.autoplay = Number(root.dataset.autoplay) || 0;
    this._timer = null;
    this._progressTl = null;
    this._active = false;

    this._bind();
    this._show(0, { instant: true });
  }

  _bind() {
    this.prevBtn?.addEventListener("click", () => { this.prev(); this._restart(); });
    this.nextBtn?.addEventListener("click", () => { this.next(); this._restart(); });
    this.dots.forEach((d) =>
      d.addEventListener("click", () => {
        const i = Number(d.dataset.i);
        if (Number.isFinite(i)) { this.goTo(i); this._restart(); }
      })
    );
    // Pause on hover (desktop)
    this.root.addEventListener("mouseenter", () => this._pauseTimer());
    this.root.addEventListener("mouseleave", () => { if (this._active) this._startTimer(); });

    // Keyboard within slideshow
    this.root.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") { e.stopPropagation(); this.prev(); this._restart(); }
      if (e.key === "ArrowRight") { e.stopPropagation(); this.next(); this._restart(); }
    });

    // Touch swipe inside slideshow (do not bubble to outer slider)
    let sx = 0, sy = 0, st = 0;
    this.root.addEventListener("touchstart", (e) => {
      const t = e.changedTouches[0];
      sx = t.clientX; sy = t.clientY; st = Date.now();
    }, { passive: true });
    this.root.addEventListener("touchend", (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - sx; const dy = t.clientY - sy; const dt = Date.now() - st;
      if (dt > 700) return;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        e.stopPropagation();
        if (dx < 0) this.next(); else this.prev();
        this._restart();
      }
    }, { passive: true });
  }

  _show(idx, { instant = false } = {}) {
    const prev = this.current;
    this.current = ((idx % this.total) + this.total) % this.total;

    // Compute "near" indices (current ± 1) for lazy-load promotion.
    const nearSet = new Set();
    if (this.total > 0) {
      nearSet.add(this.current);
      nearSet.add((this.current + 1) % this.total);
      nearSet.add((this.current - 1 + this.total) % this.total);
    }

    this.slides.forEach((s, i) => {
      const active = i === this.current;
      const isNear = nearSet.has(i);
      s.classList.toggle("is-active", active);

      // Lazy-load image: promote data-src -> src when slideshow is active
      // and slide is current/adjacent. Avoids loading imgs while lightbox closed.
      const lazyImg = s.querySelector("img.gs-media[data-src]");
      if (lazyImg && isNear && this._active) {
        lazyImg.src = lazyImg.dataset.src;
        lazyImg.removeAttribute("data-src");
      }

      // Lazy-load HTML5 video — only set src for current slide; pause others.
      const video = s.querySelector("video");
      if (video) {
        if (video.dataset.src && active && this._active) {
          video.src = video.dataset.src;
          video.removeAttribute("data-src");
        }
        if (active && this._active) video.play?.().catch(() => {});
        else { video.pause?.(); try { video.currentTime = 0; } catch (_) {} }
      }

      // Embed iframes (YouTube, Vimeo, etc) — provider-agnostic.
      // Defer src load until slideshow is active (avoids hidden background playback).
      const iframe = s.querySelector("iframe[data-src]");
      if (iframe) {
        const target = iframe.dataset.src;
        if (active && this._active) {
          if (iframe.src !== target) iframe.src = target;
        } else {
          if (iframe.src && iframe.src !== "about:blank") {
            iframe.src = "about:blank";
          }
        }
      }
    });
    this.dots.forEach((d, i) => d.classList.toggle("is-active", i === this.current));
    if (this.counterI) this.counterI.textContent = String(this.current + 1);

    if (window.gsap && !instant && !isReducedMotion()) {
      const prevSlide = this.slides[prev];
      const nextSlide = this.slides[this.current];
      if (prevSlide !== nextSlide) {
        window.gsap.fromTo(
          nextSlide,
          { opacity: 0, scale: 1.04 },
          { opacity: 1, scale: 1, duration: 0.7, ease: "expo.out" }
        );
      }
    }
  }

  next() { this._show(this.current + 1); }
  prev() { this._show(this.current - 1); }
  goTo(i) { this._show(i); }

  _startTimer() {
    if (!this.autoplay || this._timer) return;
    if (this.total <= 1) return;
    if (isReducedMotion()) return;
    
    const currentSlide = this.slides[this.current];
    if (currentSlide.dataset.type === "video" || currentSlide.dataset.type === "youtube") {
      if (this.progressBar) this.progressBar.style.width = "0%";
      return; // Pause auto-advance so user can watch the video
    }

    this._timer = setTimeout(() => {
      this._timer = null;
      this.next();
      this._startTimer();
    }, this.autoplay);
    this._animateProgress();
  }

  _pauseTimer() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    if (this._progressTl) { this._progressTl.kill(); this._progressTl = null; }
    if (this.progressBar) this.progressBar.style.width = "0%";
  }

  _restart() {
    this._pauseTimer();
    if (this._active) this._startTimer();
  }

  _animateProgress() {
    if (!this.progressBar) return;
    if (window.gsap) {
      this._progressTl = window.gsap.fromTo(
        this.progressBar,
        { width: "0%" },
        { width: "100%", duration: this.autoplay / 1000, ease: "none" }
      );
    } else {
      this.progressBar.style.transition = "none";
      this.progressBar.style.width = "0%";
      requestAnimationFrame(() => {
        this.progressBar.style.transition = `width ${this.autoplay}ms linear`;
        this.progressBar.style.width = "100%";
      });
    }
  }

  activate() {
    this._active = true;
    this._show(this.current, { instant: true });
    this._startTimer();
  }

  deactivate() {
    this._active = false;
    this._pauseTimer();
    // pause all videos and clear iframes (any provider)
    this.slides.forEach((s) => {
      s.querySelector("video")?.pause?.();
      const iframe = s.querySelector("iframe[data-src]");
      if (iframe && iframe.src && iframe.src !== "about:blank") {
        iframe.src = "about:blank";
      }
    });
  }
}

const REGISTRY = new WeakMap();

export function getSlideshow(node) {
  return REGISTRY.get(node);
}

export function initSlideshows(root = document) {
  const nodes = root.querySelectorAll("[data-slideshow]");
  const list = [];
  nodes.forEach((n) => {
    let inst = REGISTRY.get(n);
    if (!inst) {
      inst = new Slideshow(n);
      REGISTRY.set(n, inst);
    }
    list.push(inst);
  });
  return list;
}

/** Activate slideshows inside the given slide; deactivate others. */
export function activateSlideshowsIn(slideEl) {
  document.querySelectorAll("[data-slideshow]:not([data-manual-slideshow])").forEach((n) => {
    const inst = REGISTRY.get(n);
    if (!inst) return;
    if (slideEl && slideEl.contains(n)) inst.activate();
    else inst.deactivate();
  });
}
